import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack, TextInput, Textarea,
  ActionIcon, Tooltip, Center, Loader, Tabs, NumberInput, Timeline, Checkbox, Select,
  SimpleGrid, Paper, Autocomplete,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconCheck, IconBrandLine, IconWorld, IconEye, IconX, IconMessageCircle, IconRepeat, IconFlame, IconTool, IconEdit,
} from '@tabler/icons-react'
import { useBreakpoint } from '../hooks/useBreakpoint'
import SmartDateInput from '../components/SmartDateInput'
import {
  getHkOrders, createHkOrder, closeHkOrder, claimHkOrder, getInventoryItems,
  getHkOrderUpdates, addHkOrderUpdate, cancelHkOrder, markHkOrderReturned,
  updateHkOrder, getWoCategories, getLocations,
} from '../api/client'

const STATUS_COLOR: Record<string, string> = { OPEN: 'orange', IN_PROGRESS: 'blue', DONE: 'lime', CANCELLED: 'gray' }

const EMPTY = { title: '', description: '', location: '', itemName: '', qty: 1, isLoan: false, isUrgent: false, returnDueDate: '' }

export default function HkOrdersPage() {
  const { t } = useTranslation()
  const { isMobile, isSmall } = useBreakpoint()
  const [tab, setTab] = useState<string | null>('OPEN')
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'all'>('today')
  const [items, setItems] = useState<any[]>([])
  const [allItems, setAllItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [invItems, setInvItems] = useState<any[]>([])

  // ── Filters (กรองฝั่ง client จากรายการที่ดึงมาแล้วตาม tab สถานะ) ──────────
  const [searchText, setSearchText] = useState('')
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)
  const [urgentOnly, setUrgentOnly] = useState(false)
  const [loanOnly, setLoanOnly] = useState(false)

  const [createOpened, { open: openCreate, close: closeCreateModal }] = useDisclosure(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ ...EMPTY })

  const [closeOpened, { open: openCloseModal, close: closeCloseModal }] = useDisclosure(false)
  const [closingItem, setClosingItem] = useState<any>(null)
  const [closing, setClosing] = useState(false)

  const [cancelOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false)
  const [cancellingItem, setCancellingItem] = useState<any>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  const [detailOpened, { open: openDetail, close: closeDetailModal }] = useDisclosure(false)
  const [detailItem, setDetailItem] = useState<any>(null)
  const [locations, setLocations] = useState<string[]>([])
  const [woCategories, setWoCategories] = useState<{value:string,label:string}[]>([])
  const [editingHko, setEditingHko] = useState<any>(null)
  const [editHkoForm, setEditHkoForm] = useState({ title: '', description: '', location: '', qty: 1, isUrgent: false })
  const [editHkoOpened, { open: openEditHko, close: closeEditHko }] = useDisclosure(false)
  const [savingHko, setSavingHko] = useState(false)
  const [detailUpdates, setDetailUpdates] = useState<any[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)

  const handleOpenEditHko = (o: any) => {
    setEditingHko(o)
    setEditHkoForm({ title: o.title ?? '', description: o.description ?? '', location: o.location ?? '', qty: o.qty ?? 1, isUrgent: !!o.isUrgent })
    openEditHko()
  }

  const handleSaveEditHko = async () => {
    if (!editingHko) return
    setSavingHko(true)
    try {
      await updateHkOrder(editingHko.id, editHkoForm)
      notifications.show({ color: 'teal', message: `✅ ${t('hkOrders.editTitle')} #${editingHko.id}` })
      closeEditHko()
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'เกิดข้อผิดพลาด' })
    } finally { setSavingHko(false) }
  }

  useEffect(() => {
    getLocations().then(r => setLocations((r.data ?? []).filter((l: any) => l.isActive !== false).map((l: any) => l.name ?? l.code ?? '')))
    getWoCategories().then(r => setWoCategories((r.data ?? []).filter((c: any) => c.isActive !== false && (!c.appliesTo || c.appliesTo === 'HKO')).map((c: any) => ({ value: c.code, label: c.name }))))
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await getHkOrders(undefined)  // ดึงทั้งหมดเสมอ count ถูก
      setAllItems(data)
      setItems(tab === 'ALL' ? data : data.filter((x: any) => x.status === tab))
    } catch {
      notifications.show({ color: 'red', message: t('common.error') })
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])  // โหลดครั้งเดียว
  useEffect(() => {
    setItems(tab === 'ALL' ? allItems : allItems.filter((x: any) => x.status === tab))
  }, [tab, allItems])
  useEffect(() => { getInventoryItems().then(r => setInvItems(r.data)).catch(() => {}) }, [])

  // ใช้ timezone ไทย (+7) ในการเปรียบเทียบวันที่
  const todayStr = new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const weekAgo = new Date(Date.now() + 7 * 60 * 60 * 1000 - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const toThaiDate = (dt: string) =>
    dt ? new Date(new Date(dt).getTime() + 7*60*60*1000).toISOString().slice(0, 10) : ''

  const dateFilteredItems = allItems.filter((item: any) => {
    const createdDate = toThaiDate(item.createdAt)
    const isActive = item.status === 'OPEN' || item.status === 'IN_PROGRESS'
    if (dateFilter === 'today') return createdDate === todayStr || isActive
    if (dateFilter === 'week') return createdDate >= weekAgo
    return true
  })

  const filteredItems = items.filter((o) => {
    // date filter — กรองตารางให้ตรงกับ card count
    const createdDate = toThaiDate(o.createdAt)
    const isActive = o.status === 'OPEN' || o.status === 'IN_PROGRESS'
    if (dateFilter === 'today' && createdDate !== todayStr && !isActive) return false
    if (dateFilter === 'week' && createdDate < weekAgo) return false

    if (sourceFilter && o.source !== sourceFilter) return false
    if (urgentOnly && !o.isUrgent) return false
    if (loanOnly && !o.isLoan) return false
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      const haystack = `${o.title} ${o.location ?? ''} ${o.description ?? ''} ${o.itemName ?? ''}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })

  const handleCreate = async () => {
    if (!form.title) {
      notifications.show({ color: 'red', message: t('common.required') }); return
    }
    setCreating(true)
    try {
      await createHkOrder(form)
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCreateModal(); setForm({ ...EMPTY }); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setCreating(false) }
  }

  const handleClaim = async (item: any) => {
    try {
      await claimHkOrder(item.id)
      notifications.show({ color: 'lime', message: t('common.success') })
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    }
  }

  const handleClose = async () => {
    if (!closingItem) return
    setClosing(true)
    try {
      await closeHkOrder(closingItem.id)
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCloseModal(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setClosing(false) }
  }

  const openCancel = (item: any) => {
    setCancellingItem(item)
    setCancelReason('')
    openCancelModal()
  }

  const handleCancel = async () => {
    if (!cancellingItem || !cancelReason.trim()) {
      notifications.show({ color: 'red', message: t('common.required') }); return
    }
    setCancelling(true)
    try {
      await cancelHkOrder(cancellingItem.id, cancelReason.trim())
      notifications.show({ color: 'lime', message: t('common.success') })
      closeCancelModal(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setCancelling(false) }
  }

  const handleMarkReturned = async (item: any) => {
    try {
      await markHkOrderReturned(item.id)
      notifications.show({ color: 'lime', message: t('common.success') })
      load()
      if (detailItem?.id === item.id) openDetails(item)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    }
  }

  const openDetails = async (item: any) => {
    setDetailItem(item)
    setDetailUpdates([])
    setNewNote('')
    openDetail()
    setDetailLoading(true)
    try {
      const { data } = await getHkOrderUpdates(item.id)
      setDetailUpdates(data)
    } catch {
      notifications.show({ color: 'red', message: t('common.error') })
    } finally { setDetailLoading(false) }
  }

  const handleAddNote = async () => {
    if (!detailItem || !newNote.trim()) return
    setAddingNote(true)
    try {
      await addHkOrderUpdate(detailItem.id, newNote.trim())
      const { data } = await getHkOrderUpdates(detailItem.id)
      setDetailUpdates(data)
      setNewNote('')
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setAddingNote(false) }
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb={isMobile ? 'md' : 'xl'} wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
            {t('hkOrders.title')}
          </Text>
          {!isMobile && <Text size="sm" c="dimmed">{t('hkOrders.subtitle')}</Text>}
        </Box>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
          {t('hkOrders.newButton')}
        </Button>
      </Group>

      {/* ── Date Filter ── */}
      <Group gap="xs" mb="sm">
        {([
          { value: 'today', label: '📅 วันนี้' },
          { value: 'week',  label: '📆 7 วัน' },
          { value: 'all',   label: '🗂️ ทั้งหมด' },
        ] as const).map(d => (
          <Button key={d.value} size="xs"
            variant={dateFilter === d.value ? 'filled' : 'default'}
            style={dateFilter === d.value ? { background: 'var(--brand-primary,#0f172a)', color: '#fff' } : {}}
            onClick={() => setDateFilter(d.value)}>
            {d.label}
          </Button>
        ))}
      </Group>

      {/* ── Status Filter Cards ── */}
      <SimpleGrid cols={{ base: 3, sm: 5 }} spacing="xs" mb="md">
        {[
          { value: 'OPEN',        label: t('workOrders.statusOpen'),       color: '#f97316', bg: '#fff7ed', icon: '🕐' },
          { value: 'IN_PROGRESS', label: t('workOrders.statusInProgress'), color: '#3b82f6', bg: '#eff6ff', icon: '🛎️' },
          { value: 'DONE',        label: t('workOrders.statusDone'),        color: '#22c55e', bg: '#f0fdf4', icon: '✅' },
          { value: 'CANCELLED',   label: t('workOrders.statusCancelled'),   color: '#94a3b8', bg: '#f8fafc', icon: '🚫' },
          { value: 'ALL',         label: t('workOrders.statusAll'),          color: '#6366f1', bg: '#eef2ff', icon: '📋' },
        ].map(s => {
          const count = s.value === 'ALL' ? dateFilteredItems.length : dateFilteredItems.filter((w: any) => w.status === s.value).length
          const active = tab === s.value
          return (
            <Paper key={s.value} withBorder onClick={() => setTab(s.value)} style={{
              cursor: 'pointer', padding: '10px 8px', borderRadius: 10, textAlign: 'center',
              background: active ? s.color : s.bg,
              borderColor: active ? s.color : '#e2e8f0',
              transition: 'all 0.15s',
              boxShadow: active ? `0 2px 8px ${s.color}44` : 'none',
            }}>
              <Text size="lg">{s.icon}</Text>
              <Text size="xs" fw={700} style={{ color: active ? '#fff' : s.color }} mt={2}>{count}</Text>
              <Text size="xs" style={{ color: active ? '#ffffffcc' : '#64748b' }} mt={1} lineClamp={1}>{s.label}</Text>
            </Paper>
          )
        })}
      </SimpleGrid>

      <Group gap="sm" mb="md" wrap="wrap">
        <TextInput
          placeholder={t('workOrders.searchPlaceholder','Search title / location...')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
          styles={{ input: { borderColor: '#e2e8f0' } }}
        />
        <Select
          placeholder={t('workOrders.colSource')}
          clearable
          data={[{ value: 'LINE', label: 'LINE' }, { value: 'WEB', label: 'Web' }]}
          value={sourceFilter}
          onChange={setSourceFilter}
          style={{ width: 130 }}
        />
        <Checkbox
          label={t('workOrders.urgentOnlyFilter','Urgent only')}
          checked={urgentOnly}
          onChange={(e) => setUrgentOnly(e.currentTarget.checked)}
        />
        <Checkbox
          label={t('hkOrders.loanOnlyFilter','Loan items only')}
          checked={loanOnly}
          onChange={(e) => setLoanOnly(e.currentTarget.checked)}
        />
        {(searchText || sourceFilter || urgentOnly || loanOnly) && (
          <Button variant="subtle" color="gray" size="xs" leftSection={<IconX size={14} />}
            onClick={() => { setSearchText(''); setSourceFilter(null); setUrgentOnly(false); setLoanOnly(false) }}>
            {t('common.clearFilters','Clear filters')}
          </Button>
        )}
      </Group>

      {loading ? (
        <Center py={60}><Loader /></Center>
      ) : (
        <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'hidden' }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead style={{ background: '#f8f7f4' }}>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>{t('workOrders.colTitle')}</Table.Th>
                <Table.Th>{t('workOrders.colLocation')}</Table.Th>
                <Table.Th>{t('hkOrders.colQty')}</Table.Th>
                <Table.Th>{t('workOrders.colSource')}</Table.Th>
                <Table.Th>{t('workOrders.colStatus')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((o) => (
                <Table.Tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => openDetails(o)}>
                  <Table.Td><Text size="sm" c="dimmed">#{o.id}</Text></Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      {o.isUrgent && <Tooltip label={t('workOrders.urgentLabel')}><IconFlame size={16} color="#ef4444" /></Tooltip>}
                      <Text size="sm" fw={600} lineClamp={1} maw={260}>{o.title}</Text>
                      {o.isLoan && <Tooltip label={t('hkOrders.loanItem')}><IconRepeat size={14} color="#a855f7" /></Tooltip>}
                    </Group>
                    {o.reportedByName && <Text size="xs" c="dimmed">{t('workOrders.reportedBy')} {o.reportedByName}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="sm">{o.location ?? '-'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{o.qty}</Text></Table.Td>
                  <Table.Td>
                    <Tooltip label={o.source === 'LINE' ? 'LINE' : 'Web'}>
                      {o.source === 'LINE'
                        ? <IconBrandLine size={16} color="#06C755" />
                        : <IconWorld size={16} color="#94a3b8" />}
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Badge color={STATUS_COLOR[o.status]} variant="light" size="sm">
                        {t(`workOrders.status.${o.status}`)}
                      </Badge>
                      {o.isLoan && o.status === 'DONE' && (
                        <Badge color={o.returned ? 'lime' : 'orange'} variant="dot" size="sm">
                          {o.returned ? t('hkOrders.returned') : t('hkOrders.notReturned')}
                        </Badge>
                      )}
                    </Group>
                    {o.status === 'IN_PROGRESS' && o.assignedTo && (
                      <Text size="xs" c="dimmed" mt={2}>🔧 {o.assignedTo}</Text>
                    )}
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap={4}>
                      <Tooltip label={t('workOrders.viewDetails')}>
                        <ActionIcon variant="subtle" color="gray" onClick={() => openDetails(o)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {(o.status === 'OPEN' || o.status === 'IN_PROGRESS') && (
                        <Tooltip label="แก้ไข">
                          <ActionIcon variant="subtle" color="violet" onClick={(e) => { e.stopPropagation(); handleOpenEditHko(o) }}>
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}

                      {o.status === 'OPEN' && (
                        <Tooltip label={t('workOrders.claimButton')}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => handleClaim(o)}>
                            <IconTool size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {o.status !== 'DONE' && o.status !== 'CANCELLED' && (
                        <>
                          <Tooltip label={t('hkOrders.closeButton')}>
                            <ActionIcon variant="subtle" color="lime" onClick={() => { setClosingItem(o); openCloseModal() }}>
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('workOrders.cancelButton')}>
                            <ActionIcon variant="subtle" color="red" onClick={() => openCancel(o)}>
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                      {o.status === 'DONE' && o.isLoan && !o.returned && (
                        <Tooltip label={t('hkOrders.markReturned')}>
                          <ActionIcon variant="subtle" color="violet" onClick={() => handleMarkReturned(o)}>
                            <IconRepeat size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {filteredItems.length === 0 && (
            <Center py={40}><Text c="dimmed" size="sm">{t('workOrders.empty')}</Text></Center>
          )}
        </Box>
      )}

      {/* ── Modal: แก้ไขคำขอ ── */}
      <Modal opened={editHkoOpened} onClose={closeEditHko} size="md"
        title={<Text fw={600} style={{ fontSize: 18 }}>✏️ แก้ไขคำขอ #{editingHko?.id}</Text>}>
        <Stack gap="sm">
          <TextInput label={t('hkOrders.itemTitle')} value={editHkoForm.title}
            onChange={(e) => setEditHkoForm(p => ({ ...p, title: e.target.value }))} />
          <Textarea label={t('workOrders.formDescription')} minRows={2} value={editHkoForm.description}
            onChange={(e) => setEditHkoForm(p => ({ ...p, description: e.target.value }))} />
          <Autocomplete label={t('hkOrders.colLocation')} data={locations}
            value={editHkoForm.location}
            onChange={(v) => setEditHkoForm(p => ({ ...p, location: v }))} />
          <NumberInput label={t('hkOrders.colQty')} min={1} value={editHkoForm.qty}
            onChange={(v) => setEditHkoForm(p => ({ ...p, qty: Number(v) }))} />
          <Checkbox label={t('workOrders.urgentLabel')} color="red" checked={editHkoForm.isUrgent}
            onChange={(e) => setEditHkoForm(p => ({ ...p, isUrgent: e.currentTarget.checked }))} />
          <Text size="xs" c="dimmed">การแก้ไขจะถูกบันทึกในประวัติพร้อมชื่อผู้แก้ไข</Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeEditHko}>{t('common.cancel')}</Button>
            <Button loading={savingHko} onClick={handleSaveEditHko}
              style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: คำขอใหม่ ── */}
      <Modal opened={createOpened} onClose={closeCreateModal} size={isSmall ? '100%' : 'md'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('hkOrders.newButton')}</Text>}>
        <Stack gap="sm">
          <TextInput label={t('workOrders.colTitle')} value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          <Textarea label={t('workOrders.formDescription')} minRows={2} value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          <Group grow>
            <Autocomplete label={t('hkOrders.colLocation')} placeholder={t('common.locationPlaceholder')}
              data={locations} value={form.location}
              onChange={(v) => setForm((p) => ({ ...p, location: v }))} />
            <NumberInput label={t('hkOrders.colQty')} min={1} value={form.qty}
              onChange={(v) => setForm((p) => ({ ...p, qty: Number(v) || 1 }))} />
          </Group>
          <TextInput label={t('hkOrders.itemNameLabel')} placeholder={t('hkOrders.itemNamePlaceholder')} value={form.itemName}
            onChange={(e) => setForm((p) => ({ ...p, itemName: e.target.value }))} />
          <Checkbox label={t('hkOrders.isLoanLabel')} checked={form.isLoan}
            onChange={(e) => setForm((p) => ({ ...p, isLoan: e.currentTarget.checked }))} />
          {form.isLoan && (
            <SmartDateInput
              label={t('hkOrders.returnDueDateLabel', 'กำหนดคืน (ถ้ามี)')}
              placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={form.returnDueDate}
              onChange={(v) => setForm(p => ({ ...p, returnDueDate: v }))}
            />
          )}
          <Checkbox label={t('workOrders.urgentLabel')} color="red" checked={form.isUrgent}
            onChange={(e) => setForm((p) => ({ ...p, isUrgent: e.currentTarget.checked }))} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCreateModal}>{t('common.cancel')}</Button>
            <Button loading={creating} onClick={handleCreate}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: ยืนยันส่งของแล้ว ── */}
      <Modal opened={closeOpened} onClose={closeCloseModal} size={isSmall ? '100%' : 'sm'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('hkOrders.closeButton')} #{closingItem?.id}</Text>}>
        <Stack gap="sm">
          <Text size="sm">{closingItem?.title}</Text>
          {closingItem?.isLoan && (
            <Text size="xs" c="dimmed">{t('hkOrders.loanReminder')}</Text>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCloseModal}>{t('common.cancel')}</Button>
            <Button loading={closing} onClick={handleClose} color="lime">{t('hkOrders.closeButton')}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: ยกเลิก ── */}
      <Modal opened={cancelOpened} onClose={closeCancelModal} size={isSmall ? '100%' : 'sm'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('workOrders.cancelButton')} #{cancellingItem?.id}</Text>}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{cancellingItem?.title}</Text>
          <Textarea label={t('workOrders.cancelReasonLabel')} minRows={3} value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('workOrders.cancelReasonPlaceholder')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeCancelModal}>{t('common.cancel')}</Button>
            <Button loading={cancelling} onClick={handleCancel} color="red">{t('workOrders.cancelButton')}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Modal: รายละเอียด + ประวัติ ── */}
      <Modal opened={detailOpened} onClose={closeDetailModal} size={isSmall ? '100%' : 'md'}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>#{detailItem?.id} {detailItem?.title}</Text>}>
        <Stack gap="md">
          <Group gap="xs">
            <Badge color={STATUS_COLOR[detailItem?.status]} variant="light">{t(`workOrders.status.${detailItem?.status}`)}</Badge>
            {detailItem?.location && <Badge variant="light" color="gray">{detailItem.location}</Badge>}
            {detailItem?.isLoan && detailItem?.returnDueDate && (
            <Text size="sm" c="red" fw={600}>
              📅 {t('hkOrders.returnDueDateLabel', 'กำหนดคืน')}: {new Date(detailItem.returnDueDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
            </Text>
          )}
          {detailItem?.isLoan && (
              <Badge variant="light" color={detailItem.returned ? 'lime' : 'violet'}>
                {detailItem.returned ? t('hkOrders.returned') : t('hkOrders.loanItem')}
              </Badge>
            )}
          </Group>

          {detailItem?.status === 'IN_PROGRESS' && detailItem?.assignedTo && (
            <Text size="sm" c="dimmed">🔧 {t('workOrders.claimedBy')} {detailItem.assignedTo}</Text>
          )}
          {detailItem?.status === 'OPEN' && (
            <Button size="xs" variant="light" color="blue" leftSection={<IconTool size={14} />}
              onClick={async () => { await handleClaim(detailItem); openDetails({ ...detailItem, status: 'IN_PROGRESS' }) }}>
              {t('workOrders.claimButton')}
            </Button>
          )}

          <Text size="sm">{detailItem?.description}</Text>

          {detailItem?.status === 'DONE' && detailItem?.isLoan && !detailItem?.returned && (
            <Button size="xs" variant="light" color="violet" leftSection={<IconRepeat size={14} />}
              onClick={() => handleMarkReturned(detailItem)}>
              {t('hkOrders.markReturned')}
            </Button>
          )}

          <Box>
            <Text size="xs" fw={700} c="dimmed" mb={8} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('workOrders.historyTitle')}
            </Text>
            {detailLoading ? (
              <Center py={20}><Loader size="sm" /></Center>
            ) : (
              <Timeline active={detailUpdates.length} bulletSize={20} lineWidth={2}>
                {detailUpdates.map((u) => (
                  <Timeline.Item key={u.id} bullet={<IconMessageCircle size={12} />}
                    title={<Text size="sm" fw={600}>{u.authorName ?? 'System'}</Text>}>
                    <Text size="xs" c="dimmed" mb={2}>{new Date(u.createdAt).toLocaleString('th-TH')}</Text>
                    <Text size="sm">{u.note}</Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Box>

          {detailItem?.status !== 'DONE' && detailItem?.status !== 'CANCELLED' && (
            <Group align="flex-end" gap="xs">
              <Textarea flex={1} placeholder={t('workOrders.addNotePlaceholder')} minRows={1}
                value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Button loading={addingNote} onClick={handleAddNote} disabled={!newNote.trim()}
                style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
                {t('workOrders.addNoteButton')}
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>
    </Box>
  )
}
